import React, { useState, useEffect } from 'react';
import { Card, Typography, Statistic, Row, Col, Spin } from 'antd';
import { Column, Line, DualAxes } from '@ant-design/charts';
import { 
  ComposedChart, 
  Line as RechartsLine, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  Legend, 
  ResponsiveContainer 
} from 'recharts';
import { analyticsApi, PaidUsersGrowthData, NewUsersGrowthData } from '../services/api';
import * as JapaneseHolidays from 'japanese-holidays';

const { Title } = Typography;

interface CombinedUsersGrowthChartProps {
  selectedDate: string;
}

const CombinedUsersGrowthChart: React.FC<CombinedUsersGrowthChartProps> = ({ selectedDate }) => {
  const [paidGrowthData, setPaidGrowthData] = useState<PaidUsersGrowthData | null>(null);
  const [newUsersGrowthData, setNewUsersGrowthData] = useState<NewUsersGrowthData | null>(null);
  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    if (selectedDate) {
      loadGrowthData();
    }
  }, [selectedDate]);

  const loadGrowthData = async () => {
    try {
      setLoading(true);
      const [paidData, newData] = await Promise.all([
        analyticsApi.getPaidUsersGrowth(30, selectedDate),
        analyticsApi.getNewUsersGrowth(30, selectedDate)
      ]);
      setPaidGrowthData(paidData);
      setNewUsersGrowthData(newData);
    } catch (error) {
      console.error('Failed to load growth data:', error);
    } finally {
      setLoading(false);
    }
  };

  // 格式化日期
  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return `${date.getMonth() + 1}/${date.getDate()}`;
  };

  // 检查是否为日本祝日
  const checkJapaneseHoliday = (dateStr: string) => {
    const date = new Date(dateStr);
    return JapaneseHolidays.isHoliday(date);
  };

  // 准备左侧柱状图数据（新规用户和有料用户每日新增）
  const prepareColumnData = () => {
    if (!paidGrowthData || !newUsersGrowthData) return [];
    
    const result: Array<{
      date: string;
      fullDate: string;
      新規ユーザー: number;
      新増有料ユーザー: number;
    }> = [];
    
    // 创建日期映射
    const newUsersMap = new Map<string, number>();
    const paidUsersMap = new Map<string, number>();
    
    newUsersGrowthData.growthData.forEach(item => {
      newUsersMap.set(item.date, item.newUsers);
    });
    
    paidGrowthData.growthData.forEach(item => {
      paidUsersMap.set(item.date, item.newPaidUsers);
    });
    
    // 获取所有日期
    const allDates = new Set([
      ...newUsersGrowthData.growthData.map(item => item.date),
      ...paidGrowthData.growthData.map(item => item.date)
    ]);
    
    // 为每个日期创建数据点
    Array.from(allDates).sort().forEach(date => {
      result.push({
        date: formatDate(date),
        fullDate: date,
        新規ユーザー: newUsersMap.get(date) || 0,
        新増有料ユーザー: paidUsersMap.get(date) || 0,
      });
    });
    
    return result;
  };

  // 准备右侧折线图数据（累计用户数）
  const prepareLineData = () => {
    if (!paidGrowthData || !newUsersGrowthData) return [];
    
    const result: Array<{
      date: string;
      fullDate: string;
      value: number;
      type: string;
    }> = [];
    
    // 创建日期映射以确保数据对齐
    const newUsersMap = new Map<string, number>();
    newUsersGrowthData.growthData.forEach(item => {
      newUsersMap.set(item.date, item.newUsers);
    });
    
    // 获取当前选择日期的总用户数作为起点
    // 然后往回计算每天的累计总用户数
    const currentTotalUsers = newUsersGrowthData.currentTotalUsers || 0;
    let runningTotal = currentTotalUsers;
    
    // 从最后一天开始，往前计算每天的总用户数
    const sortedData = [...paidGrowthData.growthData].reverse();
    const totalUsersData: Array<{ date: string, totalUsers: number }> = [];
    
    sortedData.forEach((item, index) => {
      if (index === 0) {
        // 最后一天使用当前总用户数
        totalUsersData.unshift({ date: item.date, totalUsers: runningTotal });
      } else {
        // 往前减去前一天的新增用户数
        const newUsers = newUsersMap.get(item.date) || 0;
        runningTotal -= newUsers;
        totalUsersData.unshift({ date: item.date, totalUsers: runningTotal });
      }
    });
    
    // 重新正向遍历生成结果数据
    paidGrowthData.growthData.forEach((item, index) => {
      const formatted = formatDate(item.date);
      const totalUsersForDate = totalUsersData.find(t => t.date === item.date)?.totalUsers || 0;
      
      result.push({
        date: formatted,
        fullDate: item.date,
        value: item.cumulativePaidUsers,
        type: '累計有料ユーザー'
      });
      
      result.push({
        date: formatted,
        fullDate: item.date,
        value: totalUsersForDate,
        type: '総ユーザー数'
      });
    });
    
    return result;
  };

  // 计算统计数据
  const getStatistics = () => {
    if (!paidGrowthData || !newUsersGrowthData) {
      return { 
        totalNewUsers: 0, 
        totalNewPaidUsers: 0, 
        weeklyAverageNew: 0, 
        weeklyAveragePaid: 0 
      };
    }

    const totalNewUsers = newUsersGrowthData.total_new_users;
    const totalNewPaidUsers = paidGrowthData.total_new_paid_users;
    const weeklyAverageNew = Math.round(totalNewUsers / (newUsersGrowthData.growthData.length / 7));
    const weeklyAveragePaid = Math.round(totalNewPaidUsers / (paidGrowthData.growthData.length / 7));

    return { totalNewUsers, totalNewPaidUsers, weeklyAverageNew, weeklyAveragePaid };
  };

  const statistics = getStatistics();

  // 准备图表数据
  const columnData = prepareColumnData();
  const lineData = prepareLineData();

  // 准备柱状图数据（转换为seriesField格式）
  const prepareColumnDataForChart = () => {
    const result: Array<{
      date: string;
      fullDate: string;
      value: number;
      type: string;
    }> = [];
    
    columnData.forEach(item => {
      result.push({
        date: item.date,
        fullDate: item.fullDate,
        value: item.新規ユーザー,
        type: '新規ユーザー'
      });
      result.push({
        date: item.date,
        fullDate: item.fullDate,
        value: item.新増有料ユーザー,
        type: '新増有料ユーザー'
      });
    });
    
    return result;
  };
  
  // 柱状图配置
  const columnConfig = {
    data: prepareColumnDataForChart(),
    xField: 'date',
    yField: 'value',
    seriesField: 'type',
    isGroup: true,
    columnWidthRatio: 0.6,
    colorField: 'type',
    color: ['#52c41a', '#ff7875'],
    xAxis: {
      label: {
        style: (text: string, item: any, index: number) => {
          const chartData = prepareColumnDataForChart();
          // 由于数据是重复的（每个日期有两个数据点），需要找到对应的日期
          const dateData = chartData.find(d => d.date === text);
          if (!dateData) return { fill: '#666' };
          
          const isHoliday = checkJapaneseHoliday(dateData.fullDate);
          return {
            fill: isHoliday ? '#ff4d4f' : '#666',
            fontWeight: isHoliday ? 'bold' : 'normal'
          };
        },
      },
    },
    yAxis: {
      label: {
        formatter: (v: string) => `${v}人`,
      },
    },
    legend: {
      position: 'top-left' as const,
    },
    tooltip: {},
  };

  // 准备分离的数据 - 分别为两个系列准备数据
  const prepareSeparatedLineData = () => {
    if (!paidGrowthData || !newUsersGrowthData) return { paidData: [], totalData: [] };
    
    const paidData: Array<{
      date: string;
      fullDate: string;
      value: number;
    }> = [];
    
    const totalData: Array<{
      date: string;
      fullDate: string;
      value: number;
    }> = [];
    
    // 从lineData中分离两个系列的数据
    lineData.forEach(item => {
      if (item.type === '累計有料ユーザー') {
        paidData.push({
          date: item.date,
          fullDate: item.fullDate,
          value: item.value
        });
      } else if (item.type === '総ユーザー数') {
        totalData.push({
          date: item.date,
          fullDate: item.fullDate,
          value: item.value
        });
      }
    });
    
    return { paidData, totalData };
  };

  // 计算分离的数据
  const { paidData, totalData } = prepareSeparatedLineData();

  // 准备Recharts数据格式
  const prepareRechartsData = () => {
    if (!paidGrowthData || !newUsersGrowthData) return [];
    
    const result: Array<{
      date: string;
      fullDate: string;
      paidUsers: number;
      totalUsers: number;
    }> = [];
    
    // 创建日期映射以确保数据对齐
    const newUsersMap = new Map<string, number>();
    newUsersGrowthData.growthData.forEach(item => {
      newUsersMap.set(item.date, item.newUsers);
    });
    
    // 获取当前选择日期的总用户数作为起点
    const currentTotalUsers = newUsersGrowthData.currentTotalUsers || 0;
    let runningTotal = currentTotalUsers;
    
    // 从最后一天开始，往前计算每天的总用户数
    const sortedData = [...paidGrowthData.growthData].reverse();
    const totalUsersData: Array<{ date: string, totalUsers: number }> = [];
    
    sortedData.forEach((item, index) => {
      if (index === 0) {
        // 最后一天使用当前总用户数
        totalUsersData.unshift({ date: item.date, totalUsers: runningTotal });
      } else {
        // 往前减去前一天的新增用户数
        const newUsers = newUsersMap.get(item.date) || 0;
        runningTotal -= newUsers;
        totalUsersData.unshift({ date: item.date, totalUsers: runningTotal });
      }
    });
    
    // 生成结果数据
    paidGrowthData.growthData.forEach((item, index) => {
      const formatted = formatDate(item.date);
      const totalUsersForDate = totalUsersData.find(t => t.date === item.date)?.totalUsers || 0;
      
      result.push({
        date: formatted,
        fullDate: item.date,
        paidUsers: item.cumulativePaidUsers,
        totalUsers: totalUsersForDate,
      });
    });
    
    return result;
  };

  const rechartsData = prepareRechartsData();

  // 调试信息
  console.log('Column Data:', columnData.slice(0, 3));
  console.log('Line Data:', lineData.slice(0, 6));
  console.log('paidGrowthData:', paidGrowthData ? 'exists' : 'null');
  console.log('newUsersGrowthData:', newUsersGrowthData ? 'exists' : 'null');
  console.log('newUsersGrowthData.currentTotalUsers:', newUsersGrowthData?.currentTotalUsers);
  console.log('Paid Data:', paidData.slice(0, 3));
  console.log('Total Data:', totalData.slice(0, 3));

  // 双Y轴配置 - 简化版本
  const dualAxesConfig = {
    data: [paidData, totalData],
    xField: 'date',
    yField: ['value', 'value'],
    geometryOptions: [
      {
        geometry: 'line',
        color: '#1890ff',
      },
      {
        geometry: 'line',
        color: '#722ed1',
      },
    ],
  };

  return (
    <Card
      title={
        <div>
          <Title level={4} style={{ margin: 0 }}>ユーザー増加推移総合</Title>
          <span style={{ color: '#999', fontSize: '14px' }}>Combined Users Growth Trend</span>
        </div>
      }
      style={{ height: '480px' }}
    >
      <Spin spinning={loading}>
        {/* 统计信息显示 */}
        <Row gutter={24} style={{ marginBottom: '16px' }}>
          <Col span={6} style={{ textAlign: 'center' }}>
            <Statistic
              title="30日間新規ユーザー"
              value={statistics.totalNewUsers}
              suffix="人"
              valueStyle={{ color: '#52c41a', fontSize: '18px' }}
            />
          </Col>
          <Col span={6} style={{ textAlign: 'center' }}>
            <Statistic
              title="30日間新増有料ユーザー"
              value={statistics.totalNewPaidUsers}
              suffix="人"
              valueStyle={{ color: '#ff7875', fontSize: '18px' }}
            />
          </Col>
          <Col span={6} style={{ textAlign: 'center' }}>
            <Statistic
              title="週平均新規数"
              value={statistics.weeklyAverageNew}
              suffix="人"
              valueStyle={{ color: '#52c41a', fontSize: '18px' }}
            />
          </Col>
          <Col span={6} style={{ textAlign: 'center' }}>
            <Statistic
              title="週平均有料新増数"
              value={statistics.weeklyAveragePaid}
              suffix="人"
              valueStyle={{ color: '#ff7875', fontSize: '18px' }}
            />
          </Col>
        </Row>
        
        {/* 图表显示 - 双图表布局 */}
        <div style={{ height: '300px' }}>
          {paidGrowthData && newUsersGrowthData && columnData.length > 0 && lineData.length > 0 ? (
            <Row gutter={16}>
              <Col span={12}>
                <div style={{ textAlign: 'center', marginBottom: '8px' }}>
                  <span style={{ fontSize: '14px', fontWeight: 'bold', color: '#666' }}>
                    日次新増ユーザー数
                  </span>
                </div>
                <Column {...columnConfig} height={250} />
              </Col>
              <Col span={12}>
                <div style={{ textAlign: 'center', marginBottom: '8px' }}>
                  <span style={{ fontSize: '14px', fontWeight: 'bold', color: '#666' }}>
                    累計ユーザー数推移（双軸）
                  </span>
                </div>
                <ResponsiveContainer width="100%" height={250}>
                  <ComposedChart data={rechartsData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis 
                      dataKey="date" 
                      tick={({ x, y, payload }) => {
                        const item = rechartsData.find(d => d.date === payload.value);
                        const isHoliday = item ? checkJapaneseHoliday(item.fullDate) : false;
                        return (
                          <text 
                            x={x} 
                            y={y} 
                            dy={16} 
                            textAnchor="middle" 
                            fill={isHoliday ? '#ff4d4f' : '#666'}
                            fontWeight={isHoliday ? 'bold' : 'normal'}
                            fontSize={12}
                          >
                            {payload.value}
                          </text>
                        );
                      }}
                    />
                    <YAxis 
                      yAxisId="left" 
                      orientation="left"
                      tickFormatter={(value) => `${value}人`}
                      tick={{ fill: '#1890ff' }}
                      label={{ value: '累計有料ユーザー', angle: -90, position: 'insideLeft', style: { textAnchor: 'middle', fill: '#1890ff' } }}
                    />
                    <YAxis 
                      yAxisId="right" 
                      orientation="right"
                      tickFormatter={(value) => `${value}人`}
                      tick={{ fill: '#722ed1' }}
                      label={{ value: '総ユーザー数', angle: 90, position: 'insideRight', style: { textAnchor: 'middle', fill: '#722ed1' } }}
                    />
                    <Tooltip 
                      formatter={(value: any, name: string) => [
                        `${value}人`, 
                        name === 'paidUsers' ? '累計有料ユーザー' : '総ユーザー数'
                      ]}
                      labelFormatter={(label) => `日期: ${label}`}
                    />
                    <Legend 
                      wrapperStyle={{ paddingTop: '10px' }}
                      formatter={(value) => value === 'paidUsers' ? '累計有料ユーザー' : '総ユーザー数'}
                    />
                    <RechartsLine 
                      yAxisId="left"
                      type="monotone" 
                      dataKey="paidUsers" 
                      stroke="#1890ff" 
                      strokeWidth={2}
                      dot={{ r: 3 }}
                      name="paidUsers"
                    />
                    <RechartsLine 
                      yAxisId="right"
                      type="monotone" 
                      dataKey="totalUsers" 
                      stroke="#722ed1" 
                      strokeWidth={2}
                      dot={{ r: 3 }}
                      name="totalUsers"
                    />
                  </ComposedChart>
                </ResponsiveContainer>
              </Col>
            </Row>
          ) : (
            <div style={{ 
              height: '100%', 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center',
              color: '#999'
            }}>
              {loading ? '読み込み中...' : 'データがありません'}
            </div>
          )}
        </div>
      </Spin>
    </Card>
  );
};

export default CombinedUsersGrowthChart;